import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import SupportedSites from '../components/SupportedSites';
import { QualitySelector } from '../components/download/QualitySelector';
import { VideoMetadataCard } from '../components/download/VideoMetadataCard';
import { PlaylistView } from '../components/download/PlaylistView';
import { DownloadInfo } from '../components/download/DownloadInfo';
import { PlatformNav } from '../components/PlatformNav';
import logo from '../assets/logo.png';
import './DownloadPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

export const DownloadPage = ({
    session,
    profile,
    refreshUsage,
    customTitle = 'Download Video',
    customSubtitle,
    customPlaceholderVideo
}) => {
    const location = useLocation();

    // Toggle state
    const [downloadType, setDownloadType] = useState('video');

    // Video download state
    const [url, setUrl] = useState(location.state?.url || '');
    const [videoInfo, setVideoInfo] = useState(null);
    const [singleVideoQuality, setSingleVideoQuality] = useState('auto');
    const [playlistVideoQuality, setPlaylistVideoQuality] = useState('auto');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const lastSearchedUrl = useRef('');
    const inputRef = useRef(null);

    // Audio download state (separate)
    const [audioUrl, setAudioUrl] = useState('');
    const [audioInfo, setAudioInfo] = useState(null);
    const [audioQuality, setAudioQuality] = useState('192');
    const [audioLoading, setAudioLoading] = useState(false);
    const [audioError, setAudioError] = useState('');
    const [audioThumbSrc, setAudioThumbSrc] = useState('');
    const lastSearchedAudioUrl = useRef('');
    const audioInputRef = useRef(null);

    // Supported sites modal state
    const [showSupportedSites, setShowSupportedSites] = useState(false);

    useEffect(() => {
        if (downloadType === 'video') {
            inputRef.current?.focus();
        } else {
            audioInputRef.current?.focus();
        }
    }, [downloadType]);

    // Handle URL prefill from navigation state (e.g., from Trending page)
    useEffect(() => {
        if (location.state?.url && location.state.url !== url) {
            setUrl(location.state.url);
        }
    }, [location.state?.url]);

    // Auto-fetch metadata for Video
    useEffect(() => {
        if (!url) return;
        const isYouTubeUrl = /^(https?:\/\/(www\.)?youtube\.com\/|https?:\/\/(www\.)?youtu\.be\/)/i.test(url);
        const isInstagramUrl = /^(https?:\/\/(www\.)?instagram\.com\/)/i.test(url);
        if (!isYouTubeUrl && !isInstagramUrl) return;

        const t = setTimeout(() => {
            if (url && url !== lastSearchedUrl.current && !loading) {
                lastSearchedUrl.current = url;
                handleSearch();
            }
        }, 500);
        return () => clearTimeout(t);
    }, [url]);

    const handleSearch = async () => {
        if (!url) return;
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/metadata`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url }),
            });
            if (!res.ok) {
                throw new Error(await res.text());
            }
            const json = await res.json();
            setVideoInfo(json);
        } catch (e) {
            setError(e.message || 'Failed to fetch metadata');
            setVideoInfo(null);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (overrideUrl = null, overrideFormatId = null, overrideVideoQuality = null) => {
        const targetUrl = typeof overrideUrl === 'string' ? overrideUrl : url;
        const targetFormat = typeof overrideFormatId === 'string' ? overrideFormatId : '';
        const targetVideoQuality = typeof overrideVideoQuality === 'string' ? overrideVideoQuality : null;

        if (!session?.access_token) {
            setError('Sign in to download videos.');
            return;
        }
        setLoading(true);
        setError('');

        try {
            const payload = { url: targetUrl };
            if (targetFormat) payload.format_id = targetFormat;
            if (targetVideoQuality && !targetFormat) payload.video_quality = targetVideoQuality;

            // Step 1: Start the download job (returns immediately)
            const response = await fetch(`${API_BASE}/api/download`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const jsonError = JSON.parse(errorText);
                    throw new Error(jsonError.message || jsonError.error || 'Download failed');
                } catch (parseErr) {
                    throw new Error(errorText || 'Download failed');
                }
            }

            const spawnResult = await response.json();

            // If error on spawn
            if (spawnResult.status === 'error') {
                throw new Error(spawnResult.error || 'Download failed');
            }

            const jobId = spawnResult.job_id;
            if (!jobId) {
                throw new Error('No job ID returned');
            }

            // Step 2: Poll for completion
            let attempts = 0;
            const maxAttempts = 120; // 2 minutes max (120 * 1s)

            while (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s between polls

                const statusResponse = await fetch(`${API_BASE}/api/download/status/${jobId}`, {
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                });

                if (!statusResponse.ok) {
                    attempts++;
                    continue;
                }

                const statusResult = await statusResponse.json();

                if (statusResult.status === 'complete' && statusResult.download_url) {
                    // Download the file
                    const fileResponse = await fetch(statusResult.download_url);
                    const blob = await fileResponse.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.setAttribute('download', statusResult.filename || 'video.mp4');
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(blobUrl);
                    link.parentNode.removeChild(link);

                    refreshUsage();
                    setUrl('');
                    setVideoInfo(null);
                    return;
                }

                if (statusResult.status === 'error') {
                    throw new Error(statusResult.error || 'Download failed');
                }

                attempts++;
            }

            throw new Error('Download timed out. Please try again.');

        } catch (err) {
            try {
                const jsonError = JSON.parse(err.message);
                setError(jsonError.error || 'An unknown error occurred.');
            } catch (e) {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setUrl('');
        setVideoInfo(null);
        setSingleVideoQuality('auto');
        setPlaylistVideoQuality('auto');
        setError('');
        lastSearchedUrl.current = '';
    };

    // Audio handlers (simplified - same pattern as video)
    const handleAudioSearch = async () => {
        if (!audioUrl) return;
        setAudioError('');
        setAudioLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/metadata`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: audioUrl }),
            });
            if (!res.ok) throw new Error(await res.text());
            const json = await res.json();
            setAudioInfo(json);
        } catch (e) {
            setAudioError(e.message || 'Failed to fetch metadata');
            setAudioInfo(null);
        } finally {
            setAudioLoading(false);
        }
    };

    const handleAudioDownload = async (overrideUrl = null) => {
        const targetUrl = typeof overrideUrl === 'string' ? overrideUrl : audioUrl;

        if (!session?.access_token) {
            setAudioError('Sign in to download audio.');
            return;
        }

        if (!profile?.usage?.audio_enabled) {
            setAudioError('Audio downloads are only available on paid plans.');
            return;
        }

        setAudioLoading(true);
        setAudioError('');

        try {
            // Step 1: Start the download job
            const response = await fetch(`${API_BASE}/api/download`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    url: targetUrl,
                    download_type: 'audio',
                    audio_quality: audioQuality
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const jsonError = JSON.parse(errorText);
                    throw new Error(jsonError.message || jsonError.error || 'Download failed');
                } catch (parseErr) {
                    throw new Error(errorText || 'Download failed');
                }
            }

            const spawnResult = await response.json();

            if (spawnResult.status === 'error') {
                throw new Error(spawnResult.error || 'Download failed');
            }

            const jobId = spawnResult.job_id;
            if (!jobId) {
                throw new Error('No job ID returned');
            }

            // Step 2: Poll for completion
            let attempts = 0;
            const maxAttempts = 120;

            while (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 1000));

                const statusResponse = await fetch(`${API_BASE}/api/download/status/${jobId}`, {
                    headers: {
                        Authorization: `Bearer ${session.access_token}`,
                    },
                });

                if (!statusResponse.ok) {
                    attempts++;
                    continue;
                }

                const statusResult = await statusResponse.json();

                if (statusResult.status === 'complete' && statusResult.download_url) {
                    const fileResponse = await fetch(statusResult.download_url);
                    const blob = await fileResponse.blob();
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.setAttribute('download', statusResult.filename || 'audio.mp3');
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(blobUrl);
                    link.parentNode.removeChild(link);

                    refreshUsage();
                    setAudioUrl('');
                    setAudioInfo(null);
                    return;
                }

                if (statusResult.status === 'error') {
                    throw new Error(statusResult.error || 'Download failed');
                }

                attempts++;
            }

            throw new Error('Download timed out. Please try again.');

        } catch (err) {
            try {
                const jsonError = JSON.parse(err.message);
                setAudioError(jsonError.error || 'An unknown error occurred.');
            } catch (e) {
                setAudioError(err.message);
            }
        } finally {
            setAudioLoading(false);
        }
    };

    const handleAudioReset = () => {
        setAudioUrl('');
        setAudioInfo(null);
        setAudioQuality('192');
        setAudioError('');
        lastSearchedAudioUrl.current = '';
    };

    // Auto-fetch metadata for audio
    useEffect(() => {
        if (!audioUrl) return;
        setAudioThumbSrc('');
        const isYouTubeUrl = /^(https?:\/\/(www\.)?youtube\.com\/|https?:\/\/(www\.)?youtu\.be\/)/i.test(audioUrl);
        const isInstagramUrl = /^(https?:\/\/(www\.)?instagram\.com\/)/i.test(audioUrl);
        if (!isYouTubeUrl && !isInstagramUrl) return;

        const t = setTimeout(() => {
            if (audioUrl && audioUrl !== lastSearchedAudioUrl.current && !audioLoading) {
                lastSearchedAudioUrl.current = audioUrl;
                handleAudioSearch();
            }
        }, 500);
        return () => clearTimeout(t);
    }, [audioUrl]);

    useEffect(() => {
        if (!audioInfo?.thumbnail) return;
        const t = audioInfo.thumbnail;
        const isIgCdn = /(instagram\.com|cdninstagram|fbcdn\.net)/i.test(t);
        if (isIgCdn) {
            setAudioThumbSrc(`${API_BASE}/api/image-proxy?url=${encodeURIComponent(t)}`);
        } else {
            setAudioThumbSrc('');
        }
    }, [audioInfo?.thumbnail]);

    return (
        <div className="page-container download-page">

            {/* Header */}
            <div className="download-header">
                <h1>{downloadType === 'video' ? customTitle : 'Download Audio (MP3)'}</h1>
                {downloadType === 'video' && customSubtitle && (
                    <p>{customSubtitle}</p>
                )}
            </div>

            {/* Toggle */}
            <div className="download-type-toggle-container">
                <button
                    className={`type-toggle-btn ${downloadType === 'video' ? 'active' : ''}`}
                    onClick={() => setDownloadType('video')}
                >
                    Video
                </button>
                <button
                    className={`type-toggle-btn ${downloadType === 'audio' ? 'active' : ''}`}
                    onClick={() => setDownloadType('audio')}
                >
                    Audio
                </button>
            </div>

            {downloadType === 'video' ? (
                <div className="download-section fade-in">

                    <div className="download-form hero-search">
                        <input
                            type="text"
                            ref={inputRef}
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder={customPlaceholderVideo || "Paste a Video Link (YouTube, Instagram, TikTok + 1700+ Sites)"}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                        />
                        {!videoInfo ? (
                            <button onClick={handleSearch} disabled={loading || !url}>
                                {loading ? 'Searching...' : 'Search'}
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={() => handleDownload(null, '', singleVideoQuality)}
                                    disabled={loading || !videoInfo}
                                >
                                    {loading ? 'Converting...' : 'Download'}
                                </button>
                                <button onClick={handleReset} disabled={loading}>Reset</button>
                            </>
                        )}
                    </div>

                    <PlatformNav />

                    {error && <p className="error">Error: {error}</p>}

                    {videoInfo && (
                        videoInfo.type === 'playlist' ? (
                            <PlaylistView
                                playlist={videoInfo}
                                quality={playlistVideoQuality}
                                setQuality={setPlaylistVideoQuality}
                                onDownloadItem={(videoUrl, quality) => handleDownload(videoUrl, '', quality)}
                                loading={loading}
                            />
                        ) : (
                            <VideoMetadataCard
                                videoInfo={videoInfo}
                                quality={singleVideoQuality}
                                setQuality={setSingleVideoQuality}
                                onDownload={handleDownload}
                                loading={loading}
                            />
                        )
                    )}
                </div>
            ) : (
                <div className="download-section fade-in">

                    {!profile?.usage?.audio_enabled && (
                        <div className="audio-disabled-banner">
                            <p>Audio downloads are only available on paid plans.</p>
                            <button className="btn-secondary" onClick={() => window.location.href = '/settings'}>
                                Upgrade Plan
                            </button>
                        </div>
                    )}

                    <div className={`download-form hero-search ${!profile?.usage?.audio_enabled ? 'disabled' : ''}`}>
                        <input
                            type="text"
                            ref={audioInputRef}
                            value={audioUrl}
                            onChange={(e) => setAudioUrl(e.target.value)}
                            placeholder="Paste an Audio Link (Bandcamp, SoundCloud, Spotify, YouTube Music)"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAudioSearch();
                            }}
                            disabled={!profile?.usage?.audio_enabled}
                        />
                        {!audioInfo ? (
                            <button onClick={handleAudioSearch} disabled={audioLoading || !audioUrl || !profile?.usage?.audio_enabled}>
                                {audioLoading ? 'Searching...' : 'Search'}
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={handleAudioDownload}
                                    disabled={audioLoading || !audioInfo || !profile?.usage?.audio_enabled || profile?.usage?.audio_over_cap}
                                >
                                    {audioLoading ? 'Converting...' : 'Download'}
                                </button>
                                <button onClick={handleAudioReset} disabled={audioLoading || !profile?.usage?.audio_enabled}>Reset</button>
                            </>
                        )}
                    </div>

                    {audioError && <p className="error">Error: {audioError}</p>}

                    {profile?.usage?.audio_over_cap && (
                        <p className="limit-message">
                            Monthly audio download limit reached. Resets on {new Date(profile.usage.resets_at).toLocaleDateString()}
                        </p>
                    )}

                    {audioInfo && profile?.usage?.audio_enabled && (
                        audioInfo.type === 'playlist' ? (
                            <div className="playlist-card fade-in">
                                <div className="playlist-header">
                                    <div className="playlist-header-info">
                                        <h2>{audioInfo.title || 'Playlist'}</h2>
                                        <p>{audioInfo.entries?.length || 0} tracks found • {audioInfo.uploader || 'Unknown Uploader'}</p>
                                    </div>
                                    <div className="format-row playlist-quality-control">
                                        <label htmlFor="playlistAudioQuality">Audio Quality</label>
                                        <QualitySelector
                                            id="playlistAudioQuality"
                                            type="audio"
                                            value={audioQuality}
                                            onChange={(e) => setAudioQuality(e.target.value)}
                                            disabled={!profile?.usage?.audio_enabled}
                                        />
                                    </div>
                                </div>
                                <div className="playlist-grid">
                                    {(audioInfo.entries || []).map((track) => (
                                        <div key={track.id} className="playlist-item">
                                            <div className="history-thumb" style={{ width: '120px', height: '68px' }}>
                                                <img
                                                    src={track.thumbnail || logo}
                                                    alt={track.title}
                                                    onError={(e) => {
                                                        if (track.thumbnail && !e.target.src.includes('image-proxy')) {
                                                            e.target.src = `${API_BASE}/api/image-proxy?url=${encodeURIComponent(track.thumbnail)}`;
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className="playlist-item-info">
                                                <h3>{track.title}</h3>
                                                <p>{track.duration ? `${Math.floor(track.duration / 60)}:${(track.duration % 60).toString().padStart(2, '0')}` : ''}</p>
                                            </div>
                                            <button
                                                onClick={() => handleAudioDownload(track.url)}
                                                disabled={audioLoading || profile?.usage?.audio_over_cap}
                                            >
                                                Download MP3
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="preview-card">
                                <div
                                    className="preview-media"
                                    style={
                                        audioInfo.width && audioInfo.height
                                            ? { aspectRatio: `${audioInfo.width} / ${audioInfo.height}` }
                                            : undefined
                                    }
                                >
                                    {audioInfo.thumbnail && (
                                        <img
                                            src={audioThumbSrc || audioInfo.thumbnail}
                                            alt={audioInfo.title}
                                            onError={() => {
                                                if (!audioThumbSrc) {
                                                    setAudioThumbSrc(`${API_BASE}/api/image-proxy?url=${encodeURIComponent(audioInfo.thumbnail)}`);
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                                <div className="preview-meta">
                                    <h2>{audioInfo.title}</h2>
                                    {audioInfo.uploader && <p>by {audioInfo.uploader}</p>}
                                    <div className="format-row">
                                        <label htmlFor="audioQuality">Audio Quality:</label>
                                        <QualitySelector
                                            id="audioQuality"
                                            type="audio"
                                            value={audioQuality}
                                            onChange={(e) => setAudioQuality(e.target.value)}
                                            disabled={!profile?.usage?.audio_enabled}
                                        />
                                    </div>
                                </div>
                            </div>
                        )
                    )}
                </div>
            )}

            <DownloadInfo onSupportedSitesClick={() => setShowSupportedSites(true)} />

            <SupportedSites
                isOpen={showSupportedSites}
                onClose={() => setShowSupportedSites(false)}
            />
        </div>
    );
};
